import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardBody,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Avatar,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  HStack,
  VStack,
  useDisclosure,
  Spinner,
  Center,
} from "@chakra-ui/react";
import {
  Search,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Filter,
  List,
  ChevronDown,
} from "lucide-react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useThemeColors } from "@/hooks/useThemeColors";
import { useToastNotification } from "@/lib/utils/useToastNotification";
import { useAccessCheck } from "@/hooks/useAccess";
import DateRangePicker from "@/components/input/DateRangePicker";
import { AppointmentOverview } from "@/types/appointment";
import { getStatusColorAndBg } from "@/util/getStatusColor";
import { appointmentService } from "@/lib/services/appointmentService";
import CancelAppointmentModal from "@/components/modals/CancelAppointmentModal";
import RescheduleModal from "@/components/modals/RescheduleModal";
import DeleteAppointmentModal from "@/components/modals/DeleteAppointmentModal";
import { DoctorOverview, PatientOverview } from "@/types";
import { DataTable, DataTableOptions, DataTableRef } from "@/components/tables";
import { ActionButtons } from "@/components/common/ActionButtons";
import { exportToCSV } from "@/lib/utils/csvUtils";
import { formatDateTime } from "@/util/convertTime";

interface RescheduleData {
  requestor: string;
  appointment_date: string;
  appointment_time: string;
  reason: string;
}

// Extended interface to store full appointment data
interface FullAppointmentData {
  id: number;
  dateTime: string;
  patient: PatientOverview;
  doctor: DoctorOverview;
  service: string;
  status: string;
}

// Extended interface to store full appointment data
interface FullAppointmentData {
  id: number;
  dateTime: string;
  patient: PatientOverview;
  doctor: DoctorOverview;
  service: string;
  status: string;
}

// Helper function to parse date and time from the dateTime string
const parseDateTime = (dateTime: string) => {
  const [datePart, timePart] = dateTime.split(" - ");
  const date = datePart.trim();
  const time = timePart.trim();

  // Convert 12-hour time back to 24-hour format for input
  const convertTo24Hour = (time12h: string) => {
    if (!time12h) return "";

    const [timeStr, modifier] = time12h.split(" ");
    let [hours, minutes] = timeStr.split(":");

    if (hours === "12") {
      hours = "00";
    }

    if (modifier === "PM") {
      hours = String(parseInt(hours, 10) + 12);
    }

    return `${hours.padStart(2, "0")}:${minutes}`;
  };

  return {
    date: date,
    time: convertTo24Hour(time),
  };
};

const AppointmentPage = () => {
  const navigate = useNavigate();
  const colors = useThemeColors();
  const searchRef = useRef<HTMLInputElement>(null);
  const { showError, showSuccess, showWarning } = useToastNotification();
  const access = useAccessCheck();

  // State
  const [appointments, setAppointments] = useState<AppointmentOverview[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAppointment, setSelectedAppointment] = useState<number | null>(
    null,
  );
  const [selectedAppointmentData, setSelectedAppointmentData] =
    useState<FullAppointmentData | null>(null);
  const dateRef = useRef<HTMLInputElement>(null);
  const timeRef = useRef<HTMLInputElement>(null);
  const tableRef = useRef<DataTableRef>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [dateRange, setDateRange] = useState<{
    start_date: string | null;
    end_date: string | null;
  }>({
    start_date: null,
    end_date: null,
  });

  // Filter and Sort state
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string>("appointment_date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Cancel state
  const [cancelReason, setCancelReason] = useState("");

  // Reschedule state
  const [rescheduleData, setRescheduleData] = useState<RescheduleData>({
    requestor: "",
    appointment_date: "",
    appointment_time: "",
    reason: "",
  });
  const [rescheduleErrors, setRescheduleErrors] = useState<
    Partial<RescheduleData>
  >({});

  // Modals
  const {
    isOpen: isCancelReasonOpen,
    onOpen: onCancelReasonOpen,
    onClose: onCancelReasonClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteConfirmOpen,
    onOpen: onDeleteConfirmOpen,
    onClose: onDeleteConfirmClose,
  } = useDisclosure();
  const {
    isOpen: isRescheduleOpen,
    onOpen: onRescheduleOpen,
    onClose: onRescheduleClose,
  } = useDisclosure();

  const tableParams = useMemo(
    () => ({
      search: searchTerm,
      ...(dateRange.start_date && { start_date: dateRange.start_date }),
      ...(dateRange.end_date && { end_date: dateRange.end_date }),
      ...(statusFilter && { status: statusFilter }),
      order: sortField,
      sort: sortDirection,
    }),
    [searchTerm, dateRange, statusFilter, sortField, sortDirection],
  );

  const handleSearch = () => {
    const query = searchRef.current?.value || "";
    setSearchTerm(query);
    tableRef.current?.applyFilter("search", query);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      await exportToCSV({
        endpoint: "/appointments",
        selectedIds: [],
        searchQuery: "",
        csvHeaders: [
          "ID",
          "Patient Name",
          "Patient Phone Number",
          "Doctor Name",
          "Service",
          "Appointment Date & Time",
          "Appointment Status",
        ],
        dataMapper: (data: any) => [
          data.id || "",
          data.patient.user_fullname || "",
          data.patient.user_phone_number || "",
          data.doctor.user_fullname || "",
          data.procedures?.map(
            (p: any) => p.procedure?.name
          ).filter(Boolean).join(", ") || "",
          formatDateTime(data.appointment_datetime) || "",
          data.status || "",
        ],
        filename: `appointments_export_all_${new Date().toISOString().split("T")[0]}.csv`,
        showError,
        showSuccess,
        showWarning,
      });
    } catch (error: any) {
      showError(error);
    } finally {
      setIsExporting(false);
    }
  };
  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        appointment_datetime: {
          name: "Date & Time",
          maxWidth: "150px",
        },
        patient_html: {
          name: "Patient",
          maxWidth: "250px",
        },
        doctor_html: {
          name: "Doctor",
          maxWidth: "200px",
        },
        procedures_text: {
          name: "Service",
          maxWidth: "200px",
          className: "truncate",
        },
        status: {
          name: "Status",
          maxWidth: "100px",
          badge: {
            "1": "green",
            "0": "yellow",
          },
          badgeLabels: {
            "1": "Active",
            "0": "Inactive",
          },
        },
      },
      dataSource: "/appointments",
      primaryKey: "id",
      softDelete: true,
      displayInModal: true,
      noRecordText: "No appointments found",
    }),
    [],
  );

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access],
  );

  const handleDateRangeChange = (
    startDate: string | null,
    endDate: string | null,
  ) => {
    setDateRange({
      start_date: startDate,
      end_date: endDate,
    });
    tableRef.current?.clearPage();
  };

  const handleCancelWithReason = async () => {
    if (!selectedAppointment || !cancelReason.trim()) {
      showError("Please provide a reason for cancellation");
      return;
    }

    setIsProcessing(true);
    try {
      await appointmentService.cancelAppointment(
        selectedAppointment,
        cancelReason,
      );

      showSuccess("Success", "Appointment cancelled successfully");

      setAppointments((prev) =>
        prev.map((apt) =>
          apt.id === selectedAppointment
            ? { ...apt, status: "Cancelled" }
            : apt,
        ),
      );

      onCancelReasonClose();
      setCancelReason("");
    } catch (error: any) {
      showError(error.message || "Failed to cancel appointment");
    } finally {
      setIsProcessing(false);
      setSelectedAppointment(null);
      setSelectedAppointmentData(null);
    }
  };

  const handleDeleteAppointment = async () => {
    if (!selectedAppointment) return;

    setIsProcessing(true);
    try {
      await appointmentService.deleteAppointment(selectedAppointment);

      showSuccess("Success", "Appointment deleted successfully");

      setAppointments((prev) =>
        prev.filter((apt) => apt.id !== selectedAppointment),
      );

      onDeleteConfirmClose();
    } catch (error: any) {
      showError(error.message || "Failed to delete appointment");
    } finally {
      setIsProcessing(false);
      setSelectedAppointment(null);
      setSelectedAppointmentData(null);
    }
  };

  const validateRescheduleForm = (): boolean => {
    const errors: Partial<RescheduleData> = {};

    if (!rescheduleData.requestor.trim()) {
      errors.requestor = "Requestor name is required";
    }

    if (!rescheduleData.appointment_date) {
      errors.appointment_date = "New appointment date is required";
    } else {
      const selectedDate = new Date(rescheduleData.appointment_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        errors.appointment_date = "Date cannot be in the past";
      }
    }

    if (!rescheduleData.appointment_time) {
      errors.appointment_time = "New appointment time is required";
    }

    if (!rescheduleData.reason.trim()) {
      errors.reason = "Reason for rescheduling is required";
    }

    setRescheduleErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleRescheduleAppointment = async () => {
    if (!selectedAppointment) return;

    if (!validateRescheduleForm()) {
      return;
    }

    setIsProcessing(true);
    try {
      await appointmentService.rescheduleAppointment(
        selectedAppointment,
        rescheduleData,
      );

      showSuccess("Success", "Appointment rescheduled successfully");

      onRescheduleClose();
      setRescheduleData({
        requestor: "",
        appointment_date: "",
        appointment_time: "",
        reason: "",
      });
      setRescheduleErrors({});
    } catch (error: any) {
      showError(error.message || "Failed to reschedule appointment");
    } finally {
      setIsProcessing(false);
      setSelectedAppointment(null);
      setSelectedAppointmentData(null);
    }
  };

  return (
    <AdminLayout>
      <Box
        minH="calc(100vh - 110px)"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        bg="#FEFCF8"
        py={1}
        px={5}
      >
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card
            flexShrink={0}
            display="flex"
            flexDirection="column"
            flex="1"
            minH={0}
            overflow="hidden"
            bg="transparent"
            boxShadow="none"
            border="none"
          >
            <CardHeader
              flexShrink={0}
              py={3}
              bg="transparent"
              mt={2}
              borderRadius="lg"
            >
              <Flex
                direction={{ base: "column", lg: "row" }}
                justify="space-between"
                align={{ base: "flex-start", lg: "center" }}
                gap={{ base: 3, lg: 0 }}
              >
                <Box>
                  <Badge
                    className="rounded-sm border-[#2E37A4] border"
                    py="4px"
                    px="2px"
                    fontSize={{ base: "2xs", lg: "xs" }}
                    colorScheme="purple"
                  >
                    Total Appointment: {total}
                  </Badge>
                </Box>
                <HStack spacing={3} flexWrap="wrap" align="flex-end">
                  <ActionButtons
                    pathname="/appointments"
                    onExport={handleExportData}
                    createLabel={""}
                    isExporting={isExporting}
                  />

                  {/* <HStack
                    spacing={0}
                    bg="white"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.200"
                    overflow="hidden"
                  >
                    <IconButton
                      icon={<List size={20} />}
                      variant="ghost"
                      size="sm"
                      aria-label="List view"
                      borderRadius="0"
                      _hover={{ bg: "gray.100" }}
                    />
                    <Box w="1px" h="24px" bg="gray.200" />
                    <IconButton
                      icon={<Calendar size={20} />}
                      variant="ghost"
                      size="sm"
                      aria-label="Calendar view"
                      borderRadius="0"
                      _hover={{ bg: "gray.100" }}
                    />
                  </HStack> */}

                  {permissions.can_create && (
                    <Button
                      onClick={() =>
                        navigate("/appointments/create-appointment")
                      }
                      bg="#DC3611"
                      _hover={{ bg: "#bf3c1fff" }}
                      color="white"
                      size={{ base: "sm", md: "sm" }}
                      fontSize={{ base: "xs", md: "sm" }}
                    >
                      + New Appointment
                    </Button>
                  )}
                </HStack>
              </Flex>
            </CardHeader>

            <CardHeader
              py={1}
              pt={2}
              pb={2}
              flexShrink={0}
              display="flex"
              justifyContent="space-between"
              flexDirection={{ base: "column", lg: "row" }}
              borderRadius="lg"
            >
              <HStack spacing={4} flexWrap="wrap" alignItems={"center"}>
                <Box flex="0 1 auto" minW="250px" maxW="300px" h="32px">
                  <InputGroup size="lg" h="full">
                    <InputLeftElement pointerEvents="none" h="full">
                      <Search size={16} color="#9DA4B0" />
                    </InputLeftElement>
                    <Input
                      ref={searchRef}
                      placeholder="Search "
                      defaultValue={searchTerm}
                      onKeyDown={handleKeyDown}
                      fontSize="14px"
                      h="full"
                      bg="white"
                      borderColor="gray.200"
                    />
                  </InputGroup>
                </Box>
                <Box minW="280px" maxW="250px">
                  <DateRangePicker onDateRangeChange={handleDateRangeChange} />
                </Box>
              </HStack>
              <HStack
                spacing={4}
                flexWrap="wrap"
                alignItems={"center"}
                className="mt-3 lg:mt-0"
                bg="white"
                borderColor="gray.200"
              >
                <Menu>
                  <MenuButton
                    as={Button}
                    variant="outline"
                    size={{ base: "sm", lg: "sm" }}
                    fontSize={{ base: "xs", lg: "sm" }}
                    leftIcon={<Filter size={16} />}
                    isDisabled={!permissions.can_view}
                  >
                    Filter {statusFilter ? `(${statusFilter})` : ""}
                  </MenuButton>
                  <MenuList>
                    <MenuItem onClick={() => setStatusFilter(null)}>
                      All Status
                    </MenuItem>
                    <MenuItem onClick={() => setStatusFilter("Confirmed")}>
                      Confirmed
                    </MenuItem>
                    <MenuItem onClick={() => setStatusFilter("Checked Out")}>
                      Checked out
                    </MenuItem>
                    <MenuItem onClick={() => setStatusFilter("Checked In")}>
                      Checked in
                    </MenuItem>
                    <MenuItem onClick={() => setStatusFilter("Scheduled")}>
                      Scheduled
                    </MenuItem>

                    <MenuItem onClick={() => setStatusFilter("Cancelled")}>
                      Cancelled
                    </MenuItem>
                  </MenuList>
                </Menu>

                <Menu>
                  <MenuButton
                    as={Button}
                    variant="outline"
                    size={{ base: "sm", lg: "sm" }}
                    fontSize={{ base: "xs", lg: "sm" }}
                    leftIcon={<Calendar size={16} />}
                    rightIcon={<ChevronDown size={16} />}
                  >
                    Sort By: Recent
                  </MenuButton>
                  <MenuList>
                    <MenuItem
                      onClick={() => {
                        setSortField("appointment_date");
                        setSortDirection("desc");
                      }}
                    >
                      Recent (Newest First)
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setSortField("appointment_date");
                        setSortDirection("asc");
                      }}
                    >
                      Oldest First
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
            </CardHeader>
            <CardBody
              p={0}
              pt={1}
              pb={4}
              mx={5}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
            >
              {isLoading ? (
                <Center flex="1" p={8}>
                  <Spinner size="lg" color="blue.500" />
                </Center>
              ) : (
                <>
                  <Box
                    display="flex"
                    flexDirection="column"
                    h="100%"
                    minH={0}
                    borderRadius="lg"
                    border="1px"
                    borderColor="gray.200"
                    overflow="hidden"
                    bg="white"
                  >
                    <Box overflowX="auto" overflowY="auto" flex="1">
                      <DataTable
                        ref={tableRef}
                        options={tableOptions}
                        params={tableParams}
                        access={permissions}
                        itemClickable={true}
                        setTotal={setTotal}
                        onOpenItem={(id) =>
                          navigate(`/appointments/view-appointment/${id}`)
                        }
                        showCheckbox={false}
                        editable={false}
                      />
                    </Box>
                  </Box>
                </>
              )}
            </CardBody>
          </Card>
        </VStack>
      </Box>
      {/* Delete Appointment Modal */}
      <DeleteAppointmentModal
        isOpen={isDeleteConfirmOpen}
        onClose={onDeleteConfirmClose}
        onConfirm={handleDeleteAppointment}
        isProcessing={isProcessing}
      />

      {/* Cancel Appointment Modal */}
      <CancelAppointmentModal
        isOpen={isCancelReasonOpen}
        onClose={onCancelReasonClose}
        cancelReason={cancelReason}
        setCancelReason={setCancelReason}
        onConfirm={handleCancelWithReason}
        isProcessing={isProcessing}
      />

      {/* Reschedule Modal */}
      <RescheduleModal
        isOpen={isRescheduleOpen}
        onClose={onRescheduleClose}
        rescheduleData={rescheduleData}
        setRescheduleData={setRescheduleData}
        rescheduleErrors={rescheduleErrors}
        handleRescheduleAppointment={handleRescheduleAppointment}
        isProcessing={isProcessing}
      />
    </AdminLayout>
  );
};

export default AppointmentPage;
